// v2 sloth — the hanging long-limbed slouch brought to the ground: a hunched,
// high-arched shaggy back (a moss-tinted overlay and tufts, the algae in its
// fur), very long limbs splaying out and forward in a slow crawl, pale curved
// claw hooks on the forefeet, and the sleepy face — a pale disc with the dark
// eye-mask stripes sweeping back from each eye and a button muzzle. Two coats:
// grey-green mossy and a warmer brown. Ported 1:1 from age-of-mcp's v2/sloth.ts,
// using the library's shared `safari` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, safari, type Part } from "../../lib";

const COATS = [
  { fur: "#8b8a6d", shag: "#7c8062", limb: "#7b7a5f", face: "#d9cfae",
    mask: "#5a4f38", claw: "#d8cfae" },
  { fur: "#94846a", shag: "#86795c", limb: "#83745c", face: "#e0d4b4",
    mask: "#5e4c34", claw: "#ddd2b2" },
];

function slothParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = [
    // hunched high-arched body with a mossy shag overlay and rump
    { geometry: new THREE.IcosahedronGeometry(0.04, 0), color: c.fur,
      position: [-0.005, 0.066, 0], scale: [1.3, 1.05, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: c.shag,
      position: [-0.022, 0.084, 0], scale: [1.15, 0.78, 0.92] },
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.fur,
      position: [-0.047, 0.06, 0], scale: [1.0, 0.95, 0.95] },
    // sleepy head: pale face disc, button muzzle
    { geometry: new THREE.IcosahedronGeometry(0.0225, 0), color: c.fur,
      position: [0.042, 0.09, 0], scale: [1.0, 0.95, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.0155, 0), color: c.face,
      position: [0.0565, 0.0885, 0], scale: [0.6, 1.0, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.0048, 0), color: c.mask,
      position: [0.0655, 0.085, 0] },
  ];
  for (const s of [1, -1] as const) {
    parts.push(
      // the dark eye-mask stripe sweeping back from each eye
      { geometry: new THREE.IcosahedronGeometry(0.006, 0), color: c.mask,
        position: [0.054, 0.0905, s * 0.0095], rotation: [0, s * 0.3, 0.25],
        scale: [1.7, 0.5, 0.55] },
      // mossy shoulder tufts
      { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.shag,
        position: [0.005, 0.103, s * 0.0185], scale: [1.2, 0.7, 0.8] },
      // very long fore and hind limbs splayed out in the slow crawl
      safari.tube([0.03, 0.074, s * 0.026], [0.054, 0.005, s * 0.031],
        0.0085, 0.006, c.limb),
      safari.tube([-0.042, 0.06, s * 0.027], [-0.058, 0.005, s * 0.0305],
        0.008, 0.0058, c.limb),
      // pale curved claw hooks on the forefoot
      { geometry: new THREE.ConeGeometry(0.0028, 0.013, 4), color: c.claw,
        position: [0.063, 0.007, s * 0.0315], rotation: [0, 0, -1.95] },
    );
  }
  parts.push(...safari.eyes(0.0605, 0.0915, 0.0085, 0.0032, "#1e1812"));
  return parts;
}

export const slothV2 = defineModel({
  id: "sloth-v2",
  batchId: "fable",
  name: "Sloth (v2)",
  tags: ["creature","sloth","low-poly","age-of-mcp","fable"],
  summary: "Low-poly sloth.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.09,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => slothParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => slothParts(1) },
  ],
  animations: [],
});
